import moment from "moment-timezone";

import type { SubServiceDictionary } from "../../../../hooks/use-center";
import type { InquiryFormData } from "../../../../hooks/use-inquiries";
import type { ReferenceRecord } from "../../../../hooks/use-reference-data";

export const GENERIC_LEARNING_CENTER_NAME = "Learning Center";

export const LEARNING_CENTER_REPLACEMENT_NAMES = ["Learning Center 1 to 1", "Learning Center 4 to 1"] as const;

export const getProgramIdFromSelectedSubService = (selectedSubService?: string) =>
  selectedSubService?.split("::")[0] ?? "";

export const isGenericLearningCenterSubservice = (subServiceName?: string) =>
  subServiceName === GENERIC_LEARNING_CENTER_NAME;

export const resolveSubServiceName = (
  selectedSubService: string | undefined,
  subServices: Pick<ReferenceRecord, "name" | "value">[]
): string | undefined => {
  const programId = getProgramIdFromSelectedSubService(selectedSubService);
  if (!programId) {
    return undefined;
  }
  return subServices.find(subService => String(subService.value) === programId)?.name;
};

export const requiresLearningCenterSubserviceUpdate = (
  selectedSubService: string | undefined,
  subServices: Pick<ReferenceRecord, "name" | "value">[]
) => isGenericLearningCenterSubservice(resolveSubServiceName(selectedSubService, subServices));

export const buildLearningCenterReplacementOptions = (
  subServices: Pick<ReferenceRecord, "name" | "value">[],
  subServicesDict: SubServiceDictionary
): { name: string; value: string }[] => {
  return subServices
    .filter(subService =>
      (LEARNING_CENTER_REPLACEMENT_NAMES as readonly string[]).includes(subService.name)
    )
    .map(subService => {
      const programId = String(subService.value);
      const subjectsAndTests =
        (subServicesDict?.[programId]?.subjects?.length ?? 0) + (subServicesDict?.[programId]?.tests?.length ?? 0);
      return {
        name: subService.name,
        value: `${programId}::${subjectsAndTests}`
      };
    });
};

/** Applies a subservice change and prepares inquiry dates for the inquiry PUT API. */
export const withUpdatedSelectedSubService = (
  formData: InquiryFormData,
  selectedSubService: string,
  timezone: string
): InquiryFormData => {
  const programId = getProgramIdFromSelectedSubService(selectedSubService);
  const hasLocalDateShape = Boolean(formData.inquiryDate && formData.inquiryDate.length === 10);
  const inquiryTimestamp = hasLocalDateShape
    ? moment.tz(`${formData.inquiryDate} ${formData.inquiryTimeReceived}`, "YYYY-MM-DD HH:mm", timezone)
    : null;

  return {
    ...formData,
    selectedSubService,
    programId: programId as InquiryFormData["programId"],
    ...(inquiryTimestamp
      ? {
          inquiryDate: inquiryTimestamp.utc().format(),
          inquiryTimeReceived: inquiryTimestamp.utc().format()
        }
      : {})
  };
};
